import { c } from 'ttag';

import { BadgeCategory } from '@freelancelabs/components';

import { MembershipDisplayStatus } from '../../graphql/__generated__/schema';

/**
 * The seven states of a membership, as the `membership_status` view derives them.
 * The API returns the state, never a colour: the priority between them, disabled
 * beats revoked, revoked beats expired: lives in one SQL view so the portal and
 * the back-office always agree.
 *
 * Each state carries a **word**, not only a colour. That is the accessibility rule
 * and the mockup states it too: a status must never be signalled by hue alone, and
 * `Badge` pairs the category with its label.
 */
export const statusLabel = (status: MembershipDisplayStatus): string => {
    switch (status) {
        case MembershipDisplayStatus.Active:
            return c('Membership status').t`Active`;
        case MembershipDisplayStatus.ActiveNeverLoggedIn:
            return c('Membership status').t`Never signed in`;
        case MembershipDisplayStatus.Invited:
            return c('Membership status').t`Invited`;
        case MembershipDisplayStatus.InvitationExpired:
            return c('Membership status').t`Invitation expired`;
        case MembershipDisplayStatus.AccessRevoked:
            return c('Membership status').t`Access revoked`;
        case MembershipDisplayStatus.HasLeft:
            return c('Membership status').t`Has left`;
        case MembershipDisplayStatus.Disabled:
            return c('Membership status').t`Account disabled`;
    }
};

/**
 * `warning` marks the two states that call for an action: an invitation to resend,
 * a link that expired. `default` marks the three that are settled facts and need no
 * one's attention. Nothing is `danger`: a revoked access is a decision someone took,
 * not an incident.
 */
export const statusCategory = (status: MembershipDisplayStatus): BadgeCategory => {
    switch (status) {
        case MembershipDisplayStatus.Active:
            return 'success';
        case MembershipDisplayStatus.ActiveNeverLoggedIn:
        case MembershipDisplayStatus.Invited:
            return 'info';
        case MembershipDisplayStatus.InvitationExpired:
            return 'warning';
        case MembershipDisplayStatus.AccessRevoked:
        case MembershipDisplayStatus.HasLeft:
        case MembershipDisplayStatus.Disabled:
            return 'default';
    }
};

/** Order of the status filter: what needs attention first, settled facts last. */
export const STATUS_FILTER_ORDER: MembershipDisplayStatus[] = [
    MembershipDisplayStatus.Invited,
    MembershipDisplayStatus.InvitationExpired,
    MembershipDisplayStatus.ActiveNeverLoggedIn,
    MembershipDisplayStatus.Active,
    MembershipDisplayStatus.AccessRevoked,
    MembershipDisplayStatus.HasLeft,
    MembershipDisplayStatus.Disabled,
];
